import { type CalendarDate, parseDate } from "@internationalized/date";
import { Link, useNavigate } from "@tanstack/react-router";
import { useDebounce } from "ahooks";
import { XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Label } from "react-aria-components";

import { DatePicker } from "@/components/base/date-picker";

type FilterDateRangeProps = {
  startDate: string | null;
  endDate: string | null;
};

export function FilterDateRange({ startDate, endDate }: FilterDateRangeProps) {
  const navigate = useNavigate();

  const [localStart, setLocalStart] = useState(startDate);
  const [localEnd, setLocalEnd] = useState(endDate);
  const userChanged = useRef(false);

  const debouncedStart = useDebounce(localStart, { wait: 300 });
  const debouncedEnd = useDebounce(localEnd, { wait: 300 });

  useEffect(() => {
    if (
      userChanged.current &&
      debouncedStart === localStart &&
      debouncedEnd === localEnd &&
      (debouncedStart !== startDate || debouncedEnd !== endDate)
    ) {
      navigate({
        replace: true,
        search: (prev) => ({
          ...prev,
          page: 1,
          startDate: debouncedStart,
          endDate: debouncedEnd,
        }),
      });
    }
  }, [
    debouncedStart,
    debouncedEnd,
    localStart,
    localEnd,
    startDate,
    endDate,
    navigate,
  ]);

  // Sync local state when URL changes externally (e.g. clear filters)
  useEffect(() => {
    userChanged.current = false;
    setLocalStart(startDate);
  }, [startDate]);

  useEffect(() => {
    userChanged.current = false;
    setLocalEnd(endDate);
  }, [endDate]);

  const startValue = localStart ? parseDate(localStart) : null;
  const endValue = localEnd ? parseDate(localEnd) : null;

  const handleStartChange = (date: CalendarDate | null) => {
    userChanged.current = true;
    setLocalStart(date?.toString() ?? null);
  };

  const handleEndChange = (date: CalendarDate | null) => {
    userChanged.current = true;
    setLocalEnd(date?.toString() ?? null);
  };

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-sm font-medium">Date Range</Label>
      <div className="flex flex-row items-end gap-2">
        <DatePicker
          value={startValue}
          maxValue={endValue}
          onChange={handleStartChange}
          aria-label="Start date"
          className="flex-1"
        />
        <span className="pb-2 text-gray-500">–</span>
        <DatePicker
          minValue={startValue}
          value={endValue}
          onChange={handleEndChange}
          aria-label="End date"
          className="flex-1"
        />
        {(localStart || localEnd) && (
          <Link
            onClick={() => {
              setLocalStart(null);
              setLocalEnd(null);
            }}
            search={(search) => ({
              ...search,
              page: 1,
              startDate: null,
              endDate: null,
            })}
            className="p-1 pb-2 text-gray-500 hover:text-gray-700"
            aria-label="Clear date range"
          >
            <XIcon size={16} />
          </Link>
        )}
      </div>
    </div>
  );
}
